import { Injectable, OnDestroy, NgZone } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface SelectionState {
  text: string;
  /** Viewport-relative rect of the selection range — used for fixed-position tooltip. */
  rect: DOMRect;
}

@Injectable({ providedIn: 'root' })
export class WordSelectionLookupService implements OnDestroy {

  private _selection$ = new BehaviorSubject<SelectionState | null>(null);
  readonly selection$ = this._selection$.asObservable();

  private _enabled = false;
  private _onMouseUp   = this._handleMouseUp.bind(this);
  private _onMouseDown = this._handleMouseDown.bind(this);

  constructor(private _zone: NgZone) {}

  enable(): void {
    if (this._enabled) return;
    this._enabled = true;
    document.addEventListener('mouseup',   this._onMouseUp,   true);  // capture: fires first
    document.addEventListener('mousedown', this._onMouseDown, false); // bubble: popover can stopPropagation
  }

  disable(): void {
    if (!this._enabled) return;
    this._enabled = false;
    document.removeEventListener('mouseup',   this._onMouseUp,   true);
    document.removeEventListener('mousedown', this._onMouseDown, false);
    this._selection$.next(null);
  }

  clear(): void {
    this._selection$.next(null);
  }

  ngOnDestroy(): void {
    this.disable();
  }

  private _handleMouseUp(): void {
    // Small delay so the browser has committed the final selection
    setTimeout(() => {
      this._zone.run(() => {
        const sel  = window.getSelection();
        const text = sel?.toString().trim() ?? '';

        if (!text) {
          this._selection$.next(null);
          return;
        }

        const range = sel?.getRangeAt(0);
        if (!range) {
          this._selection$.next(null);
          return;
        }

        this._selection$.next({ text, rect: range.getBoundingClientRect() });
      });
    }, 10);
  }

  private _handleMouseDown(): void {
    this._zone.run(() => this._selection$.next(null));
  }
}
